import * as React from "react";
import { GetFaQsByEventQuery } from "@src/@types/api.d";
import { useFaqList } from "@src/hooks/useFaqList";
import { motion } from "framer-motion";

export type ITemplate16FAQ = {
  eventId: number;
  faqData: GetFaQsByEventQuery;
  loading?: boolean;
};

const FAQ: React.FC<ITemplate16FAQ> = ({ eventId, faqData }) => {
  const {
    faqItems,
    paginatedFaqItems,
    currentPage,
    totalPages,
    handlePageChange,
  } = useFaqList({ faqData, itemsPerPage: 3 });

  // Return null if no FAQs exist
  if (!faqItems.length) return null;

  return (
    <div className="w-full py-16 lg:py-20 overflow-hidden relative text-center bg-[rgba(42,46,50,1)]">
      {/* Header */}
      <div className="text-center mb-12 relative z-10 px-6">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-white text-3xl md:text-4xl lg:text-5xl font-normal leading-tight tracking-wide font-andalusia"
        >
          Q & A
        </motion.h2>
      </div>

      {/* FAQ Container */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex flex-col gap-8 md:gap-10 lg:gap-12 min-h-[350px] md:min-h-[450px] justify-center">
          {paginatedFaqItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              className="pb-8 md:pb-10 border-b border-white"
            >
              {/* Question */}
              <motion.h3
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 + 0.2 }}
                className="text-white text-lg md:text-xl lg:text-2xl font-semibold leading-tight tracking-wide mb-3 md:mb-4 font-brooklyn uppercase"
              >
                {item.question}
              </motion.h3>

              {/* Answer */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 + 0.3 }}
                className="text-white text-sm md:text-base lg:text-lg leading-relaxed font-inter opacity-90 whitespace-pre-line"
              >
                {item.answer}
              </motion.p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex justify-center items-center gap-2 md:gap-3 mt-12 md:mt-16 relative z-15"
        >
          {/* Previous Button */}
          <button
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 md:px-5 md:py-2.5 rounded-lg bg-[rgba(42,46,50,1)] text-white border-2 border-white font-semibold cursor-pointer transition-all duration-300 hover:enabled:bg-white hover:enabled:text-[rgba(42,46,50,1)] disabled:opacity-50 disabled:cursor-not-allowed font-inter"
            aria-label="Previous page"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Next Button */}
          <button
            onClick={() =>
              handlePageChange(Math.min(totalPages, currentPage + 1))
            }
            disabled={currentPage === totalPages}
            className="px-4 py-2 md:px-5 md:py-2.5 rounded-lg bg-[rgba(42,46,50,1)] text-white border-2 border-white font-semibold cursor-pointer transition-all duration-300 hover:enabled:bg-white hover:enabled:text-[rgba(42,46,50,1)] disabled:opacity-50 disabled:cursor-not-allowed font-inter"
            aria-label="Next page"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default FAQ;
